import { appendLassoSample, isScreenDragActive, normalizeScreenBounds, screenBoundsHasPositiveArea, screenBoundsToRing, screenPointsBounds, validateAndSimplifyScreenLasso, } from "./screen-region-selection.js";
/**
 * Pure CSS-pixel region capture state.
 *
 * The session never touches MapLibre, Store, History or SelectionController.
 * Adapters own pointer capture, rendering, candidate resolution and selection
 * application.
 */
export class ScreenSelectionRegionSession {
    #kind;
    #intent;
    #boxActivationThreshold;
    #lassoSampleSpacing;
    #status = "armed";
    #points = [];
    #rejection;
    #revision = 0;
    constructor(kind, intent, options = {}) {
        this.#kind = kind;
        this.#intent = intent;
        this.#boxActivationThreshold = options.boxActivationThreshold ?? 4;
        this.#lassoSampleSpacing = options.lassoSampleSpacing ?? 2;
        if (!Number.isFinite(this.#boxActivationThreshold) ||
            this.#boxActivationThreshold < 0) {
            throw new RangeError("Box activation threshold must be non-negative.");
        }
        if (!Number.isFinite(this.#lassoSampleSpacing) ||
            this.#lassoSampleSpacing < 0) {
            throw new RangeError("Lasso sample spacing must be non-negative.");
        }
    }
    snapshot() {
        const points = Object.freeze(this.#points.map((point) => ({ ...point })));
        const bounds = points.length > 0 ? screenPointsBounds(points) : undefined;
        return Object.freeze({
            kind: this.#kind,
            status: this.#status,
            intent: this.#intent,
            points,
            ...(bounds !== undefined ? { bounds: Object.freeze({ ...bounds }) } : {}),
            ...(this.#rejection !== undefined
                ? { rejection: Object.freeze({ ...this.#rejection }) }
                : {}),
            revision: this.#revision,
        });
    }
    get kind() {
        return this.#kind;
    }
    get intent() {
        return this.#intent;
    }
    get status() {
        return this.#status;
    }
    pointerDown(point) {
        this.#points = [{ ...point }];
        this.#rejection = undefined;
        this.#status = this.#kind === "lasso" ? "active" : "armed";
        this.#revision += 1;
        return this.snapshot();
    }
    pointerMove(point) {
        if (this.#points.length === 0)
            return this.snapshot();
        if (this.#kind === "box") {
            const start = this.#points[0];
            this.#points = [{ ...start }, { ...point }];
            this.#status = isScreenDragActive(start, point, this.#boxActivationThreshold)
                ? "active"
                : "armed";
        }
        else {
            this.#points = [
                ...appendLassoSample(this.#points, point, this.#lassoSampleSpacing),
            ];
            this.#status = "active";
        }
        this.#rejection = undefined;
        this.#revision += 1;
        return this.snapshot();
    }
    pointerUp(point) {
        if (this.#points.length === 0) {
            return Object.freeze({ completed: false, noop: true });
        }
        return this.#kind === "box"
            ? this.#completeBox(point)
            : this.#completeLasso(point);
    }
    reset() {
        this.#points = [];
        this.#rejection = undefined;
        this.#status = "armed";
        this.#revision += 1;
        return this.snapshot();
    }
    #completeBox(point) {
        const start = this.#points[0];
        if (!isScreenDragActive(start, point, this.#boxActivationThreshold)) {
            this.reset();
            return Object.freeze({ completed: false, noop: true });
        }
        const bounds = normalizeScreenBounds(start, point);
        if (!screenBoundsHasPositiveArea(bounds)) {
            this.reset();
            return Object.freeze({ completed: false, noop: true });
        }
        const ring = screenBoundsToRing(bounds);
        const completion = Object.freeze({
            completed: true,
            kind: "box",
            intent: this.#intent,
            points: Object.freeze([{ ...start }, { ...point }]),
            ring: Object.freeze(ring.map((candidate) => ({ ...candidate }))),
            bounds: Object.freeze({ ...bounds }),
        });
        this.reset();
        return completion;
    }
    #completeLasso(point) {
        const points = [
            ...appendLassoSample(this.#points, point, Number.EPSILON),
        ];
        const result = validateAndSimplifyScreenLasso(points);
        if (!result.valid) {
            this.#points = result.points.map((candidate) => ({ ...candidate }));
            this.#rejection = result.rejection;
            this.#status = "rejected";
            this.#revision += 1;
            return Object.freeze({
                completed: false,
                noop: false,
                rejection: Object.freeze({ ...result.rejection }),
            });
        }
        const completion = Object.freeze({
            completed: true,
            kind: "lasso",
            intent: this.#intent,
            points: Object.freeze(result.points.map((candidate) => ({ ...candidate }))),
            ring: Object.freeze(result.ring.map((candidate) => ({ ...candidate }))),
            bounds: Object.freeze({ ...result.bounds }),
        });
        this.reset();
        return completion;
    }
}
//# sourceMappingURL=screen-selection-region-session.js.map